import React from "react";
import { useNavigate } from "react-router-dom";
import useCart from "../hooks/useCart";
import useAuth from "../hooks/useAuth";
import { formatMaterialDisplayLabel } from "../utils/materialLabels";
import { bindStorefrontRefresh } from "../utils/storefrontRefresh";
import { calculateShippingPrice } from "../utils/orderPricing";
import "./CartPage.css";
import TermsAndConditions from "./TermsAndConditions.jsx";

const CartPage = () => {
  const { cart, removeFromCart, updateQuantity, getTotalPrice, clearCart, syncCartProducts } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [showTermsModal, setShowTermsModal] = React.useState(false);
  const getStockQuantity = (product = {}) => {
    const parsedStockQuantity = Number(product?.stockQuantity);
    return Number.isFinite(parsedStockQuantity) ? Math.max(0, Math.floor(parsedStockQuantity)) : 0;
  };
  const getSelectableSizes = (product = {}) => (
    Array.isArray(product?.availableSizes)
      ? product.availableSizes
        .map((size) => String(size || '').trim())
        .filter((size) => {
          const normalizedSize = String(size || '').trim().toUpperCase();
          return Boolean(normalizedSize && normalizedSize !== 'NA' && normalizedSize !== 'N/A');
        })
      : []
  );
  const getCartItemSizeDisplay = (item = {}) => {
    const selectedSize = String(item.selectedSize || '').trim();
    if (selectedSize) {
      return selectedSize;
    }

    const selectableSizes = getSelectableSizes(item.product);
    if (selectableSizes.length === 1) {
      return selectableSizes[0];
    }

    return selectableSizes.length > 1 ? '' : 'NA';
  };
  const hasInvalidStockInCart = cart.some((item) => {
    const stockQuantity = getStockQuantity(item.product);
    return stockQuantity <= 0 || item.quantity > stockQuantity;
  });

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  React.useEffect(() => {
    if (user) {
      syncCartProducts();
    }
  }, [user, syncCartProducts]);

  React.useEffect(() => {
    if (!user) {
      return undefined;
    }

    return bindStorefrontRefresh(() => {
      syncCartProducts();
    }, { scopes: ["products"] });
  }, [user, syncCartProducts]);

  const handleQuantityChange = (productId, newQuantity, selectedSize = '') => {
    updateQuantity(productId, parseInt(newQuantity), selectedSize);
  };

  const handleRemoveItem = (productId, selectedSize = '') => {
    removeFromCart(productId, selectedSize);
  };

  const handleCheckout = () => {
    if (hasInvalidStockInCart) {
      window.alert("One or more items in your cart are out of stock or exceed the available quantity.");
      return;
    }

    setShowTermsModal(true);
  };
  const handleAcceptTerms = () => {
    setShowTermsModal(false);
    navigate('/checkout');
  };

  const handleContinueShopping = () => {
    navigate("/store");
  };

  if (loading) {
    return <div className="cart-loading">Loading...</div>;
  }

  if (!user) {
    return null; // Will redirect in useEffect
  }

  const cartSubtotal = getTotalPrice();
  const shippingPrice = calculateShippingPrice(cartSubtotal);
  const cartTotal = cartSubtotal + shippingPrice;

  return (
    <div className="cart-page-container">
      {showTermsModal && (
        <div className="terms-modal-overlay">
          <div className="terms-modal-shell">
            <button
              type="button"
              className="terms-modal-close"
              aria-label="Close terms and conditions"
              onClick={() => setShowTermsModal(false)}
            >
              &times;
            </button>
            <TermsAndConditions onDone={handleAcceptTerms} />
          </div>
        </div>
      )}
      <h1>Your Shopping Cart</h1>

      {cart.length === 0 ? (
        <div className="empty-cart">
          <p>Your cart is empty</p>
          <button onClick={handleContinueShopping} className="continue-shopping">
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="cart-content">
          <div className="cart-items">
            <div className="cart-header">
              <span className="header-product">Product</span>
              <span className="header-price">Price</span>
              <span className="header-quantity">Quantity</span>
              <span className="header-total">Total</span>
              <span className="header-action">Action</span>
            </div>

            {cart.map((item) => (
              <div key={`${item.product._id}-${item.selectedSize || 'default'}`} className="cart-item">
                <div className="item-product">
                  <img src={item.product.images[0]} alt={item.product.title} />
                  <div className="item-details">
                    <h3>{item.product.title}</h3>
                    <p>{formatMaterialDisplayLabel(item.product.material)}</p>
                    {getCartItemSizeDisplay(item) ? <p>Size: {getCartItemSizeDisplay(item)}</p> : null}
                    {getStockQuantity(item.product) <= 0 ? (
                      <p style={{ color: "#b23d3d", fontWeight: 600 }}>Out of stock</p>
                    ) : item.quantity > getStockQuantity(item.product) ? (
                      <p style={{ color: "#b23d3d", fontWeight: 600 }}>
                        Only {getStockQuantity(item.product)} item(s) available. Please reduce quantity.
                      </p>
                    ) : getStockQuantity(item.product) <= 5 ? (
                      <p style={{ color: "#9a5d00", fontWeight: 600 }}>
                        Only {getStockQuantity(item.product)} item(s) left in stock.
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="item-price">₹{item.product.offerPrice}</div>

                <div className="item-quantity">
                  <button
                    onClick={() => handleQuantityChange(item.product._id, item.quantity - 1, item.selectedSize)}
                    className="quantity-btn"
                    disabled={item.quantity <= 1}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    min="1"
                    max={getStockQuantity(item.product) > 0 ? getStockQuantity(item.product) : undefined}
                    value={item.quantity}
                    onChange={(e) =>
                      handleQuantityChange(item.product._id, e.target.value, item.selectedSize)
                    }
                  />
                  <button
                    onClick={() => handleQuantityChange(item.product._id, item.quantity + 1, item.selectedSize)}
                    className="quantity-btn"
                    disabled={getStockQuantity(item.product) <= 0 || item.quantity >= getStockQuantity(item.product)}
                  >
                    +
                  </button>
                </div>

                <div className="item-total">
                  ₹{(item.product.offerPrice * item.quantity).toFixed(2)}
                </div>

                <div className="item-action">
                  <button
                    onClick={() => handleRemoveItem(item.product._id, item.selectedSize)}
                    className="remove-btn"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{`\u20B9${cartSubtotal.toFixed(2)}`}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>FREE</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{`\u20B9${cartTotal.toFixed(2)}`}</span>
            </div>

            <div className="coupon-code">
              <input type="text" placeholder="Enter coupon code" />
              <button>Apply</button>
            </div>

            <button onClick={handleCheckout} className="checkout-btn" disabled={hasInvalidStockInCart}>
              Proceed to Checkout
            </button>

            <div className="cart-actions">
              <button onClick={handleContinueShopping} className="continue-shopping">
                Continue Shopping
              </button>
              <button onClick={clearCart} className="clear-cart">
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;
